import { observer } from "mobx-react-lite";
import { useMemo, useRef } from "react";

import { RadioGroup, RadioGroupItem } from "../base/radio-group";
import { Label } from "./label";
import {
  EnumValues,
  FieldProps,
  useField,
  useModelBinding,
  valuesToMap,
} from "./utils";
import { ValidationTooltip } from "./validation-tooltip";

interface Props<M extends object, V extends string | number> extends FieldProps<
  M,
  V
> {
  values: EnumValues<V>;
  required?: boolean;
  reverse?: boolean;
  disabled?: boolean;
}

export const Radio = observer(function Radio<
  M extends object,
  V extends string | number,
>({ values, required, reverse, disabled, ...restProps }: Props<M, V>) {
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  const { value: currentValue, onChange } = useModelBinding(restProps);
  const { isInvalid, tooltip } = useField(hiddenInputRef, currentValue);

  const valuesMap = useMemo(() => valuesToMap(values), [values]);

  // Convert value to string for the hidden input
  const stringValue = currentValue != null ? String(currentValue) : "";

  return (
    <ValidationTooltip model={tooltip}>
      <input
        ref={hiddenInputRef}
        type="text"
        value={stringValue}
        onChange={() => {}}
        required={required}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />
      <RadioGroup
        value={currentValue != null ? String(currentValue) : undefined}
        onValueChange={(newValue) => {
          onChange(
            (typeof [...valuesMap.keys()][0] === "number"
              ? parseInt(newValue)
              : newValue) as V,
          );
        }}
        disabled={disabled}
        className="flex gap-3"
      >
        {[...valuesMap.entries()].map(([value, displayValue]) => (
          <div key={value} className="flex items-center gap-1.5">
            {!reverse && <Label htmlFor={String(value)}>{displayValue}:</Label>}
            <RadioGroupItem
              value={String(value)}
              id={String(value)}
              aria-invalid={isInvalid}
            />
            {reverse && <Label htmlFor={String(value)}>{displayValue}</Label>}
          </div>
        ))}
      </RadioGroup>
    </ValidationTooltip>
  );
});
